import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Check, Loader2, XCircle } from "lucide-react";

const SignupSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeRegistration } = useAuth();
  const [status, setStatus] = useState("verifying");
  const polled = useRef(false);

  useEffect(() => {
    if (polled.current) return;
    polled.current = true;

    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;

    const poll = async () => {
      try {
        const result = await completeRegistration(sessionId);
        
        if (result.status === "paid") {
          setStatus("success");
          setTimeout(() => navigate("/onboarding", { replace: true }), 2000);
          return;
        }
        
        if (result.status === "expired") {
          setStatus("expired");
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        setStatus("error");
      }
    };

    poll();
  }, [searchParams, completeRegistration, navigate]);

  return (
    <div className="min-h-screen bg-space-900 flex items-center justify-center" data-testid="signup-success-page">
      <div className="text-center p-8 max-w-md">
        {status === "verifying" && (
          <>
            <Loader2 className="w-16 h-16 text-teal-400 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Setting up your account...</h1>
            <p className="text-slate-400">Payment confirmed. Creating your workspace now.</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-teal-400/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-teal-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Welcome to Voxinity AI!</h1>
            <p className="text-slate-400">Your account is ready. Redirecting to dashboard...</p>
          </>
        )}

        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">
              {status === "expired" ? "Checkout Expired" : "Something went wrong"}
            </h1>
            <p className="text-slate-400 mb-6">
              {status === "expired" 
                ? "Your checkout session expired. Please try signing up again."
                : "We couldn't verify your payment. Please contact support or try again."}
            </p>
            <button 
              onClick={() => navigate("/signup")}
              className="px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
              data-testid="try-again-btn"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SignupSuccessPage;
